<template>
  <div class="chat-simulator">
    <b-row>
      <b-col md="8">
        <b-card>
          <b-card-header>
            <h5 class="mb-0">
              <feather-icon icon="MessageSquareIcon" class="mr-2" />
              Agendamento de Mensagens para Live
            </h5>
          </b-card-header>
          <b-card-body>
            <b-form @submit.prevent="addScheduledMessage">
              <!-- Usuário removido, API usará o usuário autenticado -->
              <b-form-group label="Mensagem:">
                <b-form-textarea v-model="scheduledMessage.content" rows="3" required />
              </b-form-group>
              <b-form-group label="Quando a mensagem será enviada:">
                <b-row>
                  <b-col cols="6">
                    <b-form-input
                      v-model.number="scheduledMessage.minutes"
                      type="number"
                      min="0"
                      placeholder="Minutos"
                      required
                    />
                  </b-col>
                  <b-col cols="6">
                    <b-form-input
                      v-model.number="scheduledMessage.seconds"
                      type="number"
                      min="0"
                      max="59"
                      placeholder="Segundos"
                      required
                    />
                  </b-col>
                </b-row>
              </b-form-group>
              <b-button type="submit" variant="info" block :disabled="!scheduledMessage.content">
                <feather-icon icon="ClockIcon" class="mr-1" />
                Adicionar Mensagem
              </b-button>
            </b-form>
            <hr>
            <div>
              <template v-if="scheduledMessages.length">
                <h6>Mensagens Agendadas:</h6>
                <b-table :items="scheduledMessagesWithMinutes" :fields="tableFields" small responsive>
                  <template #cell(actions)="{ item, index }">
                    <b-button size="sm" variant="primary" class="mr-1" @click="editScheduledMessage(index)">Editar</b-button>
                    <b-button size="sm" variant="danger" @click="removeScheduledMessage(index)">Remover</b-button>
                  </template>
                </b-table>
              </template>
              <template v-else>
                <div class="text-muted mt-3">Nenhuma mensagem agendada.</div>
              </template>
              <b-button variant="success" block class="mt-2" @click="saveAllMessages">
                <feather-icon icon="SaveIcon" class="mr-1" />
                Salvar Agendamento (JSON)
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      
      <b-col md="4">
        <b-card>
          <b-card-header>
            <h5 class="mb-0">
              <feather-icon icon="PlayIcon" class="mr-2" />
              Preview do Vídeo
            </h5>
          </b-card-header>
          <b-card-body>
            <div v-if="currentVideoUrl" class="video-preview">
              <video
                ref="videoPlayer"
                :src="currentVideoUrl"
                controls
                class="w-100"
                style="max-height: 300px;"
                @timeupdate="updateCurrentTime"
                @loadedmetadata="onVideoLoaded"
              >
                Seu navegador não suporta o elemento de vídeo.
              </video>
              <div class="mt-2 text-center">
                <small class="text-muted">
                  Tempo atual: {{ formatTime(currentTime) }}
                  <span v-if="videoDuration > 0"> / {{ formatTime(videoDuration) }}</span>
                </small>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                class="mt-2 w-100"
                @click="useCurrentTime"
                :disabled="!currentTime"
              >
                <feather-icon icon="ClockIcon" class="mr-1" />
                Usar Tempo Atual ({{ formatTime(currentTime) }})
              </b-button>
            </div>
            <div v-else class="text-muted text-center py-4">
              <feather-icon icon="VideoOffIcon" size="2x" class="mb-2" />
              <p>Nenhum vídeo selecionado</p>
              <small>Selecione um vídeo na configuração de stream para visualizar aqui</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, BInputGroup, BInputGroupAppend, BRow, BCol } from 'bootstrap-vue'
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'

export default {
  name: 'ChatSimulator',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BRow,
    BCol,
  },
  props: {
    isStreaming: {
      type: Boolean,
      default: false,
    },
    streamKey: {
      type: String,
      default: '',
    },
    videoFile: {
      type: File,
      default: null,
    },
    videoUrl: {
      type: String,
      default: '',
    },
    webinarioData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      scheduledMessage: {
        content: '',
        minutes: '',
        seconds: '',
      },
      scheduledMessages: [],
      tableFields: [
        { key: 'content', label: 'Mensagem' },
        { key: 'delayMinutes', label: 'Tempo' },
        { key: 'actions', label: 'Ações' },
      ],
      editingIndex: null,
      currentTime: 0,
      currentVideoUrl: null, // URL atual do vídeo (pode ser blob ou AWS)
      videoDuration: 0, // Duração total do vídeo em segundos
    }
  },
  computed: {
    scheduledMessagesWithMinutes() {
      // Exibe delay no formato mm:ss
      return this.scheduledMessages.map(msg => {
        const totalSeconds = Math.floor((msg.delay || 0) / 1000)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        const pad = n => n.toString().padStart(2, '0')
        return {
          ...msg,
          delayMinutes: `${pad(minutes)}:${pad(seconds)}`,
        }
      })
    },
    autoMessageCount() {
      return this.chatMessages.filter(msg => msg.type === 'auto').length
    },
    uniqueUsers() {
      const users = new Set(this.chatMessages.map(msg => msg.username))
      return users.size
    },
  },
  watch: {
    videoFile: {
      handler(newFile) {
        console.log('ChatSimulator - videoFile mudou:', newFile)
        this.updateVideoUrl()
      },
      immediate: true,
    },
    videoUrl: {
      handler(newUrl) {
        console.log('ChatSimulator - videoUrl mudou:', newUrl)
        this.updateVideoUrl()
      },
      immediate: true,
    },
    isStreaming(newVal) {
      if (newVal && this.autoMessages.enabled) {
        this.startAutoMessages()
      } else {
        this.stopAutoMessages()
      }
    },
    autoMessagesEnabled(newVal) {
      if (newVal && this.isStreaming) {
        this.startAutoMessages()
      } else {
        this.stopAutoMessages()
      }
    },
    webinarioData: {
      handler(newWebinario) {
        if (newWebinario && newWebinario.chat) {
          this.loadWebinarioChatMessages(newWebinario.chat)
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadScheduledMessages()
    this.updateVideoUrl() // Inicializa o vídeo no mounted
  },
  methods: {
    updateVideoUrl() {
      console.log('updateVideoUrl chamado - videoFile:', this.videoFile, 'videoUrl:', this.videoUrl)
      
      if (this.videoFile) {
        // Se tem arquivo local, usa ele (prioritário)
        this.currentVideoUrl = URL.createObjectURL(this.videoFile)
        this.videoDuration = 0
        console.log('Usando videoFile:', this.currentVideoUrl)
      } else if (this.videoUrl) {
        // Se não tem arquivo local mas tem URL do AWS, usa a URL
        this.currentVideoUrl = this.videoUrl
        this.videoDuration = 0
        console.log('Usando videoUrl:', this.currentVideoUrl)
      } else {
        this.currentVideoUrl = null
        this.videoDuration = 0
        console.log('Nenhum vídeo disponível')
      }
    },
    addScheduledMessage() {
      if (!this.scheduledMessage.content) return
      
      // Converte minutos e segundos para milissegundos
      const delayMs = ((this.scheduledMessage.minutes || 0) * 60 + (this.scheduledMessage.seconds || 0)) * 1000
      const delaySeconds = delayMs / 1000
      
      // Validação: verifica se o tempo agendado não excede a duração do vídeo
      if (this.videoDuration > 0 && delaySeconds > this.videoDuration) {
        this.$toast.error(`O tempo agendado não pode exceder a duração do vídeo (${this.formatTime(this.videoDuration)})`)
        return
      }
      
      if (this.editingIndex !== null) {
        // Atualiza mensagem existente
        this.$set(this.scheduledMessages, this.editingIndex, {
          content: this.scheduledMessage.content,
          delay: delayMs,
        })
        this.editingIndex = null
        this.$toast.success('Mensagem editada!')
      } else {
        // Adiciona nova mensagem
        this.scheduledMessages.push({
          content: this.scheduledMessage.content,
          delay: delayMs,
        })
        this.$toast.success('Mensagem agendada!')
      }
      this.scheduledMessage = { content: '', minutes: 0, seconds: 0 }
    },
    editScheduledMessage(index) {
      const msg = this.scheduledMessages[index]
      const totalSeconds = Math.floor((msg.delay || 0) / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      this.scheduledMessage = { content: msg.content, minutes, seconds }
      this.editingIndex = index
    },
    removeScheduledMessage(index) {
      this.scheduledMessages.splice(index, 1)
    },
    async saveAllMessages() {
      try {
        // Salva localmente
        localStorage.setItem('chat_scheduled_messages', JSON.stringify(this.scheduledMessages))
        // Pega o user_id
        const userData = localStorage.getItem('userData')
        const userId = userData ? JSON.parse(userData).id : null

        // Monta o payload
        const payload = {
          user_id: userId,
          messages: this.scheduledMessages,
        }

        // Envia para o backend
        await youtubeService.saveScheduledMessages(payload)
        this.$emit('chat-saved') // Emite evento para indicar que o chat foi salvo
      } catch (error) {
        this.$toast.error(error.message || 'Erro ao enviar mensagens ao backend')
      }
    },
    loadScheduledMessages() {
      const saved = localStorage.getItem('chat_scheduled_messages')
      if (saved) {
        this.scheduledMessages = JSON.parse(saved)
      }
    },
    loadWebinarioChatMessages(chatMessages) {
      console.log('ChatSimulator - Carregando mensagens do webinário:', chatMessages)
      
      if (Array.isArray(chatMessages) && chatMessages.length > 0) {
        // Converte as mensagens do webinário para o formato do componente
        this.scheduledMessages = chatMessages.map(msg => ({
          content: msg.content,
          delay: parseInt(msg.delay, 10) || 0,
        }))
        
        // Salva no localStorage também
        localStorage.setItem('chat_scheduled_messages', JSON.stringify(this.scheduledMessages))
  
      }
    },
    updateCurrentTime() {
      if (this.$refs.videoPlayer) {
        this.currentTime = this.$refs.videoPlayer.currentTime
      }
    },
    onVideoLoaded() {
      if (this.$refs.videoPlayer) {
        this.videoDuration = this.$refs.videoPlayer.duration
        console.log('Duração do vídeo carregada:', this.videoDuration)
      }
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },
    useCurrentTime() {
      if (this.currentTime > 0) {
        const totalSeconds = Math.floor(this.currentTime)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        this.scheduledMessage.minutes = minutes
        this.scheduledMessage.seconds = seconds
        this.$toast.info(`Tempo definido para: ${this.formatTime(this.currentTime)}`)
      }
    },
  },
  
  // Não é mais necessário beforeDestroy
}
</script>

<style lang="scss" scoped>
.chat-simulator {
  .chat-container {
    height: 400px;
    display: flex;
    flex-direction: column;
    
    .chat-messages {
      flex: 1;
      overflow-y: auto;
      padding: 1rem;
      background-color: #f8f9fa;
      border-radius: 0.375rem;
      margin-bottom: 1rem;
      
      .chat-message {
        margin-bottom: 1rem;
        padding: 0.75rem;
        background-color: white;
        border-radius: 0.375rem;
        border-left: 4px solid #007bff;
        
        &.auto {
          border-left-color: #28a745;
        }
        
        &.scheduled {
          border-left-color: #ffc107;
        }
        
        .message-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
          
          .username {
            font-weight: 600;
            color: #007bff;
          }
          
          .timestamp {
            font-size: 0.875rem;
            color: #6c757d;
          }
        }
        
        .message-content {
          color: #212529;
        }
      }
    }
    
    .chat-input {
      .username-input {
        max-width: 150px;
      }
    }
  }
  
  .chat-stats {
    .stat-item {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      
      .stat-label {
        font-weight: 500;
      }
      
      .stat-value {
        color: #6c757d;
      }
    }
  }
}
</style>